import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Layers, Clock, Filter, Dumbbell, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { CATEGORIES, CATEGORY_LABELS } from "@/lib/workoutTaxonomy";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_LABELS = {
    non_member: "Members",
    member: "Members",
    member_plus: "Member+",
};

export default function AthleteWorkouts() {
    const { user, loading, activeAthleteId } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    // Parent running a specific kid's workouts on this device — access is
    // keyed to THAT athlete's subscription server-side.
    // Falls back to AuthContext's resolved active athlete so parents who
    // land here directly (no ?athlete_id= deep link) still get gated by
    // their kid's subscription instead of the "no-athlete-context = -1"
    // path. Sachin 2026-02-17.
    const athleteId = searchParams.get("athlete_id") || activeAthleteId || "";
    const [rows, setRows] = useState([]);
    const [busy, setBusy] = useState(true);
    // Initialize category from the URL so deep links like
    // /dashboard/workouts?category=strength (used by TrainingCategories
    // cards) actually filter the list — local state without URL-sync was
    // silently dropping the param. (Sachin 2026-02-16.)
    const [category, setCategory] = useState(searchParams.get("category") || "");
    // Strength sub-tab: filters within the Strength library. URL-synced so
    // deep-links like /dashboard/workouts?category=strength&body_focus=upper_body
    // open straight to the Upper Body tab.
    const [bodyFocus, setBodyFocus] = useState(searchParams.get("body_focus") || "");

    useEffect(() => {
        if (!loading && !user) navigate("/", { replace: true });
    }, [loading, user, navigate]);

    const refresh = async () => {
        setBusy(true);
        try {
            const params = {};
            if (category) params.category = category;
            // Only send body_focus when filtering Strength — backend silently
            // accepts it on other categories but it would return 0 results.
            if (category === "strength" && bodyFocus) params.body_focus = bodyFocus;
            if (athleteId) params.athlete_id = athleteId;
            const r = await axios.get(`${API}/workouts`, { params, withCredentials: true });
            setRows(r.data || []);
        } catch (e) {
            toast.error("Couldn't load workouts", { description: e?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        // Refresh whenever the active filters change. We also defensively
        // clear body_focus if the athlete switched out of strength so we
        // don't accidentally narrow other categories to zero results.
        if (category !== "strength" && bodyFocus) setBodyFocus("");
        if (user) refresh();
    }, [user, category, bodyFocus, athleteId]);

    if (loading || !user) return null;

    const allLocked = rows.length > 0 && rows.every((w) => w.locked);

    return (
        <div data-testid="athlete-workouts" className="bg-brand-obsidian min-h-screen">
            <section className="pt-32 pb-8 border-b border-zinc-900">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <Link to="/dashboard" className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 hover:text-brand-red inline-flex items-center gap-1.5 mb-5">
                        <ArrowLeft className="w-3 h-3" /> Dashboard
                    </Link>
                    <div className="text-[11px] tracking-[0.3em] uppercase text-brand-red mb-3 inline-flex items-center gap-3">
                        <span className="inline-block w-10 h-px bg-brand-red" /> Programming
                    </div>
                    <h1 className="font-display text-5xl md:text-6xl tracking-wider leading-[0.92] text-white">
                        YOUR <span className="text-brand-red">WORKOUTS.</span>
                    </h1>
                </div>
            </section>

            <section className="py-5 border-b border-zinc-900 sticky top-16 md:top-20 bg-brand-obsidian/95 backdrop-blur z-30">
                <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-wrap items-center gap-1.5">
                    <Filter className="w-3.5 h-3.5 text-zinc-300 mr-1" />
                    {/* "All" filter removed per Sachin 2026-02-27 — programs-first
                        flow means an athlete arrives at this page already filtered
                        to a specific category from TrainingCategories cards. */}
                    {CATEGORIES.map((c) => (
                        <button key={c.value} data-testid={`athlete-filter-${c.value}`} onClick={() => setCategory(c.value)} className={`text-[10px] tracking-[0.2em] uppercase border px-3 py-2 ${category === c.value ? "border-brand-red text-brand-red" : "border-zinc-800 text-zinc-200 hover:text-white"}`}>{c.label}</button>
                    ))}
                </div>
                {category === "strength" && (
                    <div
                        data-testid="strength-body-focus-tabs"
                        className="max-w-7xl mx-auto px-6 md:px-10 mt-3 flex flex-wrap items-center gap-1.5"
                    >
                        <span className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mr-1">Focus</span>
                        {[
                            { value: "", label: "All" },
                            { value: "full_body", label: "Full Body" },
                            { value: "lower_body", label: "Lower Body" },
                            { value: "upper_body", label: "Upper Body" },
                        ].map((opt) => (
                            <button
                                key={opt.value}
                                data-testid={`strength-focus-${opt.value || "all"}`}
                                onClick={() => setBodyFocus(opt.value)}
                                className={`text-[10px] tracking-[0.2em] uppercase border px-3 py-1.5 ${
                                    bodyFocus === opt.value
                                        ? "border-sky-500 text-sky-300 bg-sky-950/20"
                                        : "border-zinc-800 text-zinc-200 hover:text-white"
                                }`}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                )}
            </section>

            <section className="py-8 pb-32 md:pb-12">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    {!busy && allLocked && (
                        <div data-testid="workouts-members-only-banner" className="mb-6 border border-amber-500/40 bg-amber-950/20 p-5 flex items-start gap-3">
                            <Lock className="w-4 h-4 text-amber-300 mt-0.5 shrink-0" />
                            <div>
                                <div className="text-amber-200 text-sm font-semibold tracking-wide">Workouts unlock with an active subscription</div>
                                <div className="text-zinc-300 text-sm mt-1">Once your weekly subscription is active, every workout shown here unlocks automatically. <Link to="/dashboard/billing" className="underline">Manage subscription</Link>.</div>
                            </div>
                        </div>
                    )}
                    {busy ? (
                        <div className="py-16 flex items-center justify-center"><Loader2 className="w-6 h-6 text-brand-red animate-spin" /></div>
                    ) : rows.length === 0 ? (
                        <div className="border border-zinc-900 bg-zinc-950 p-12 text-center">
                            <Dumbbell className="w-7 h-7 text-zinc-200 mx-auto mb-3" />
                            <div className="font-display text-2xl tracking-wider text-white">No workouts yet</div>
                            <div className="text-sm text-zinc-300 mt-2">Your coach hasn&apos;t published any in this category.</div>
                        </div>
                    ) : (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {rows.map((w) => {
                                const blockCount = (w.blocks || []).length;
                                if (w.locked) {
                                    return (
                                        <div
                                            key={w.workout_id}
                                            data-testid={`athlete-workout-locked-${w.workout_id}`}
                                            aria-disabled="true"
                                            className="border border-zinc-900 bg-zinc-950/60 p-6 opacity-50 cursor-not-allowed relative"
                                            title={`Requires ${TIER_LABELS[w.tier_required] || w.tier_required} tier`}
                                        >
                                            <div className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase border border-amber-500/40 text-amber-300 px-2 py-1">
                                                <Lock className="w-3 h-3" /> {TIER_LABELS[w.tier_required] || w.tier_required}
                                            </div>
                                            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-3">{CATEGORY_LABELS[w.category]}</div>
                                            <div className="font-display text-2xl tracking-wider text-zinc-400 leading-tight">{w.title}</div>
                                            <div className="mt-4 text-xs text-zinc-500 flex items-center gap-3">
                                                <span className="inline-flex items-center gap-1"><Layers className="w-3 h-3" /> {blockCount} block{blockCount === 1 ? "" : "s"}</span>
                                                {w.estimated_duration && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {w.estimated_duration} min</span>}
                                            </div>
                                        </div>
                                    );
                                }
                                return (
                                    <Link
                                        key={w.workout_id}
                                        to={`/dashboard/workouts/${w.workout_id}${athleteId ? `?athlete_id=${encodeURIComponent(athleteId)}` : ""}`}
                                        data-testid={`athlete-workout-${w.workout_id}`}
                                        style={{ touchAction: "manipulation" }}
                                        className="block border border-zinc-900 bg-zinc-950 hover:border-brand-red transition-colors p-6 group active:bg-zinc-900 select-none"
                                    >
                                        <div className="text-[10px] tracking-[0.25em] uppercase text-brand-red mb-3">{CATEGORY_LABELS[w.category]}</div>
                                        <div className="font-display text-2xl tracking-wider text-white group-hover:text-brand-red transition-colors leading-tight">{w.title}</div>
                                        <div className="mt-4 text-xs text-zinc-300 flex items-center gap-3">
                                            <span className="inline-flex items-center gap-1"><Layers className="w-3 h-3" /> {blockCount} block{blockCount === 1 ? "" : "s"}</span>
                                            {w.estimated_duration && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {w.estimated_duration} min</span>}
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}
